/**
 * `Annotations_Executor.ts` — port of Unity's
 * `Executor/Annotations_Executor.cs`. Only `create_annotation` is ported; see
 * `registry/Annotations_Registry.ts`.
 *
 * Unity anchors at the pointer hit when the user is pointing at the part, or
 * did within the last 5 s, and the web keeps that rule (user direction,
 * 2026-10-29): the viewport's hit point reaches here through the context
 * engine (`getRecentPointedSurfaces`). Deviation: otherwise Unity anchors at
 * the part's pivot and floats the bubble 0.4 m up and 0.1 m forward, which can
 * land inside the part, behind other geometry or off screen. The web anchors
 * on a point of the part the user can see and puts the bubble where its whole
 * card is in clear sight (user direction, 2026-10-27) —
 * `runtime/annotations/annotationPlacement.ts`.
 *
 * Web-first: `get_annotations`, `edit_annotation`, `resolve_annotation` and
 * `move_annotation` (see `registry/Annotations_Registry.ts`). A web comment
 * stores no part, so the list reads it off the geometry its anchor sits on
 * (`runtime/annotations/annotationAnchorPart.ts`). A comment another client
 * attached to a part carries that part, in whose LOCAL space its positions
 * are stored — so it is listed by that part and never moved from here, which
 * would write world positions into a part-local row.
 */

import { Ioc } from "@core/ioc/ioc";
import type { IAIContextEngineModel } from "@ai/context/AIContextEngineModel.Core";
import type { PointedSurface } from "@ai/context/AIContextEngineModel.SceneProjectUserContext";
import type { IProjectModel } from "@models/project/IProjectModel";
import {
  ANNOTATIONS_MODEL,
  type IAnnotationsModel,
} from "@models/annotations/IAnnotationsModel";
import { AUTH_MODEL, type IAuthModel } from "@models/auth/IAuthModel";
import {
  annotationAuthorFromUser,
  type AnnotationData,
  type AnnotationVec3,
} from "@models/data/annotationData";
import { buildSceneMergedAncestorIndex, type EntitySnapshot } from "@models/data/entityData";
import { findAnnotationPlacement } from "@runtime/annotations/annotationPlacement";
import { findAnnotationAnchorParts } from "@runtime/annotations/annotationAnchorPart";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import {
  buildPathIndex,
  displayNameOf,
  resolveManyByPath,
  type PathEntry,
} from "@ai/context/hierarchyPaths";
import {
  getBool,
  getString,
  getStringList,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

/** Comments `get_annotations` lists, newest first; the rest are counted. */
const K_MAX_LISTED_ANNOTATIONS = 50;
/** Replies listed under a comment, most recent last; the earlier ones are counted. */
const K_MAX_LISTED_REPLIES = 5;
/** Characters of a comment or reply the list quotes. */
const K_TEXT_PREVIEW_MAX = 200;
/** Characters of a comment a write result quotes to name it. */
const K_LABEL_PREVIEW_MAX = 60;
/** The author id the Comment tool and the comments list fall back to when signed out. */
const K_LOCAL_USER_ID = "local-user";

interface FoundAnnotation {
  readonly sceneId: string;
  readonly annotations: IAnnotationsModel;
  readonly annotation: AnnotationData;
}

export class AnnotationsExecutor implements IDomainExecutor {
  private readonly m_project: IProjectModel;
  private readonly m_contextEngine: IAIContextEngineModel;

  constructor(project: IProjectModel, contextEngine: IAIContextEngineModel) {
    this.m_project = project;
    this.m_contextEngine = contextEngine;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.CreateAnnotation:
        return this.createAnnotation(args);
      case AgentToolNames.GetAnnotations:
        return this.getAnnotations();
      case AgentToolNames.EditAnnotation:
        return this.editAnnotation(args);
      case AgentToolNames.ResolveAnnotation:
        return this.resolveAnnotations(args);
      case AgentToolNames.MoveAnnotation:
        return this.moveAnnotation(args);
      default:
        return null;
    }
  }

  private async createAnnotation(args: ToolArgs): Promise<string> {
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return "error: no active scene";

    const partPath = getString(args, "partPath");
    if (!partPath) return "error: must provide partPath";
    const text = getString(args, "value") ?? "";

    const [entry] = resolveManyByPath(this.m_project.getEntities(sceneId), [partPath]);
    if (!entry) return `error: part with path '${partPath}' not found`;
    const partName = displayNameOf(entry.snapshot);

    if (!Ioc.isRegistered<IAnnotationsModel>(ANNOTATIONS_MODEL)) {
      return "error: annotations model not available";
    }
    const annotations = Ioc.resolve<IAnnotationsModel>(ANNOTATIONS_MODEL);

    const partIds = subtreeIds(entry.snapshot);
    const pointedAnchor = this.pointedPointOn(partIds);
    const placement = findAnnotationPlacement({
      partId: entry.snapshot.id,
      partIds,
      ignoredIds: new Set(annotations.getAnnotations(sceneId).map((a) => a.id)),
      text,
      pointedAnchor,
    });
    if (placement.kind === "viewport-unavailable") {
      return "tool_error: create_annotation_unavailable. The 3D viewport is not active.";
    }
    if (placement.kind === "no-bounds") {
      return `tool_error: create_annotation_no_geometry. Part '${partName}' has no visible geometry to attach a comment to.`;
    }
    const user = Ioc.isRegistered<IAuthModel>(AUTH_MODEL)
      ? Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser
      : null;

    const created = annotations.createAnnotationAt(
      sceneId,
      text,
      annotationAuthorFromUser(user),
      placement.anchor,
      placement.placement,
    );
    // A comment the user asked for is one they expect to see; the Comment
    // tool shows the layer on activation for the same reason.
    annotations.setAnnotationsVisible(true);
    // The id lets a follow-up ("change it to…", "move it to…") act on this
    // comment without listing them first.
    const where = pointedAnchor ? " where the user is pointing" : "";
    const ok = `ok: created annotation on part '${partName}'${where} (annotationId:"${created.id}")`;
    // The agent should know when the user cannot see the result from here,
    // or it will describe a comment they are not looking at.
    return placement.anchorVisible ? ok : `${ok}. ${notVisibleNote(pointedAnchor !== null)}`;
  }

  private getAnnotations(): string {
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return "error: no active scene";
    const annotations = annotationsModel();
    if (!annotations) return "error: annotations model not available";

    const all = annotations.getAnnotations(sceneId);
    // A sketch whose drawing has not finished uploading is hidden everywhere.
    const listed = all
      .filter((a) => a.type !== "sketch" || a.sketchUploaded !== false)
      .sort((a, b) => timeOf(b.createdAt) - timeOf(a.createdAt));
    if (listed.length === 0) return "No comments in this scene.";
    const shown = listed.slice(0, K_MAX_LISTED_ANNOTATIONS);
    const parts = this.partsOf(sceneId, shown, new Set(all.map((a) => a.id)));
    const userId = currentUserId();

    const lines = [
      `${listed.length} ${listed.length === 1 ? "comment" : "comments"} in this scene, newest first:`,
    ];
    shown.forEach((annotation, i) => {
      lines.push(...formatAnnotationLines(annotation, parts[i] ?? null, userId));
    });
    if (listed.length > shown.length) {
      lines.push(`(${listed.length - shown.length} older comments not listed)`);
    }
    return lines.join("\n");
  }

  /**
   * The part each comment is attached to, or null when it is on none. A
   * comment another client attached names its part; a web comment's part is
   * read off the surface its anchor sits on.
   */
  private partsOf(
    sceneId: string,
    listed: ReadonlyArray<AnnotationData>,
    ignoredIds: ReadonlySet<string>,
  ): Array<PathEntry | null> {
    const entities = this.m_project.getEntities(sceneId);
    // A part folded into a merged node is addressed by that node, as in
    // `Get_Scene_Context`, which lists the node and not the parts inside it.
    const mergedAncestors = buildSceneMergedAncestorIndex(entities);
    const bySnapshotId = new Map(buildPathIndex(entities).map((e) => [e.snapshot.id, e]));
    const entryOf = (id: string | null | undefined): PathEntry | null =>
      id ? bySnapshotId.get(mergedAncestors.get(id) ?? id) ?? null : null;

    // A sketch's pose is the camera that drew it, not a point on a part.
    const anchors = listed.map((a) =>
      a.type === "text" && !a.parentSnapshotId ? a.attachmentPosition ?? null : null);
    const probed = anchors.filter((a): a is AnnotationVec3 => a !== null);
    const candidateIds = [...bySnapshotId.keys()].filter((id) => !mergedAncestors.has(id));
    const hits = probed.length > 0
      ? findAnnotationAnchorParts(probed, ignoredIds, candidateIds)
      : null;
    let next = 0;
    return listed.map((annotation, i) => {
      if (annotation.parentSnapshotId) return entryOf(annotation.parentSnapshotId);
      if (!anchors[i]) return null;
      return entryOf(hits?.[next++] ?? null);
    });
  }

  private editAnnotation(args: ToolArgs): string {
    const found = this.findAnnotation(args);
    if (typeof found === "string") return found;
    const { sceneId, annotations, annotation } = found;
    const text = getString(args, "value");
    if (!text) return "error: must provide value";
    if (annotation.type === "sketch") {
      return "tool_error: edit_annotation_sketch. That comment is a sketch, which has no text to edit.";
    }
    // Authorship, as the comment context menu gates Edit: a Commenter may
    // edit their own comment and nobody else's.
    if (annotation.creator.id !== currentUserId()) {
      return `tool_error: edit_annotation_not_author. Only ${annotation.creator.name || "its author"} can edit that comment.`;
    }
    if (annotation.isResolved) {
      return `tool_error: edit_annotation_resolved. That comment is resolved. Reopen it with ${AgentToolNames.ResolveAnnotation} first if the user wants it changed.`;
    }
    annotations.updateComment(sceneId, annotation.id, text);
    annotations.setAnnotationsVisible(true);
    return "ok: comment updated";
  }

  private resolveAnnotations(args: ToolArgs): string {
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return "error: no active scene";
    const annotations = annotationsModel();
    if (!annotations) return "error: annotations model not available";
    const ids = [...new Set(getStringList(args, "annotationIds").map(normalizeAnnotationId))]
      .filter(Boolean);
    if (ids.length === 0) return "error: must provide annotationIds";
    const resolved = getBool(args, "resolved") ?? true;

    const byId = new Map(annotations.getAnnotations(sceneId).map((a) => [a.id, a]));
    const changed: string[] = [];
    const unchanged: string[] = [];
    const missing: string[] = [];
    for (const id of ids) {
      const annotation = byId.get(id);
      if (!annotation) {
        missing.push(id);
      } else if (annotation.isResolved === resolved) {
        unchanged.push(labelOf(annotation));
      } else {
        annotations.setResolved(sceneId, id, resolved);
        changed.push(labelOf(annotation));
      }
    }
    // A reopened comment is back in the scene; show the layer it is on.
    if (!resolved && changed.length > 0) annotations.setAnnotationsVisible(true);

    if (changed.length === 0 && unchanged.length === 0) {
      return `error: ${missingNote(missing)}`;
    }
    const lines: string[] = [];
    if (changed.length > 0) {
      lines.push(`ok: ${resolved ? "resolved" : "reopened"} ${changed.join(", ")}`);
    }
    if (unchanged.length > 0) {
      lines.push(`${changed.length > 0 ? "" : "ok: "}already ${resolved ? "resolved" : "open"}: ${unchanged.join(", ")}`);
    }
    if (missing.length > 0) lines.push(missingNote(missing));
    return lines.join("\n");
  }

  private moveAnnotation(args: ToolArgs): string {
    const found = this.findAnnotation(args);
    if (typeof found === "string") return found;
    const { sceneId, annotations, annotation } = found;
    if (annotation.type === "sketch") {
      return "tool_error: move_annotation_sketch. That comment is a sketch, which stays where it was drawn.";
    }
    if (annotation.parentSnapshotId) {
      return "tool_error: move_annotation_attached_elsewhere. That comment was attached to its part in another Campfire app (desktop or headset) and can only be moved there.";
    }
    if (annotation.isResolved) {
      return `tool_error: move_annotation_resolved. That comment is resolved. Reopen it with ${AgentToolNames.ResolveAnnotation} first if the user wants it moved.`;
    }

    const partPath = getString(args, "partPath");
    if (!partPath) return "error: must provide partPath";
    const [entry] = resolveManyByPath(this.m_project.getEntities(sceneId), [partPath]);
    if (!entry) return `error: part with path '${partPath}' not found`;
    const partName = displayNameOf(entry.snapshot);

    const partIds = subtreeIds(entry.snapshot);
    const pointedAnchor = this.pointedPointOn(partIds);
    const placement = findAnnotationPlacement({
      partId: entry.snapshot.id,
      partIds,
      ignoredIds: new Set(annotations.getAnnotations(sceneId).map((a) => a.id)),
      text: annotation.text,
      pointedAnchor,
    });
    if (placement.kind === "viewport-unavailable") {
      return "tool_error: move_annotation_unavailable. The 3D viewport is not active.";
    }
    if (placement.kind === "no-bounds") {
      return `tool_error: move_annotation_no_geometry. Part '${partName}' has no visible geometry to attach a comment to.`;
    }
    annotations.reanchorAnnotation(sceneId, annotation.id, placement.anchor, placement.placement);
    annotations.setAnnotationsVisible(true);
    const where = pointedAnchor ? " where the user is pointing" : "";
    const ok = `ok: moved the comment to part '${partName}'${where}`;
    return placement.anchorVisible ? ok : `${ok}. ${notVisibleNote(pointedAnchor !== null)}`;
  }

  /**
   * Where the user is pointing on this part, or pointed in the last few
   * seconds (Unity `TryGetRecentIndexRaycastHitOnPart`); null when they point
   * at none of it. Deviation: Unity forgets the earlier point as soon as the
   * pointer rests on something else; the web keeps it, so a cursor that drifts
   * off while Spark is still thinking does not lose the spot.
   */
  private pointedPointOn(partIds: ReadonlySet<string>): PointedSurface["point"] | null {
    return this.m_contextEngine
      .getRecentPointedSurfaces()
      .find((surface) => partIds.has(surface.snapshotId))?.point ?? null;
  }

  /** The comment `annotationId` names in the active scene, or the error to return. */
  private findAnnotation(args: ToolArgs): FoundAnnotation | string {
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return "error: no active scene";
    const annotations = annotationsModel();
    if (!annotations) return "error: annotations model not available";
    const id = normalizeAnnotationId(getString(args, "annotationId") ?? "");
    if (!id) return "error: must provide annotationId";
    const annotation = annotations.getAnnotations(sceneId).find((a) => a.id === id);
    return annotation ? { sceneId, annotations, annotation } : `error: ${missingNote([id])}`;
  }
}

function annotationsModel(): IAnnotationsModel | null {
  return Ioc.isRegistered<IAnnotationsModel>(ANNOTATIONS_MODEL)
    ? Ioc.resolve<IAnnotationsModel>(ANNOTATIONS_MODEL)
    : null;
}

/** The signed-in user's id, as the comments list and context menu compare it. */
function currentUserId(): string {
  return Ioc.isRegistered<IAuthModel>(AUTH_MODEL)
    ? Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.userId ?? K_LOCAL_USER_ID
    : K_LOCAL_USER_ID;
}

function notVisibleNote(pointed: boolean): string {
  return pointed
    ? "The point the user pointed at is not visible from their current view."
    : "The part is not visible from the user's current view, so the comment is attached at its centre.";
}

function missingNote(ids: ReadonlyArray<string>): string {
  return `no comment with id ${ids.map((id) => `'${id}'`).join(", ")} in this scene. Call ${AgentToolNames.GetAnnotations} for the current ids.`;
}

/** Accepts an id pasted with the list's `annotationId:"…"` wrapper still on it. */
function normalizeAnnotationId(raw: string): string {
  return raw.trim().replace(/^annotationId\s*:\s*/i, "").replace(/^["']|["']$/g, "").trim();
}

/** A comment's line and its replies' lines, in the form `get_annotations` documents. */
function formatAnnotationLines(
  annotation: AnnotationData,
  part: PathEntry | null,
  userId: string,
): string[] {
  const states = [`by ${authorOf(annotation.creator, userId)}`];
  if (annotation.type === "sketch") {
    states.push("sketch");
  } else if (part) {
    states.push(`on "${displayNameOf(part.snapshot)}" at ${part.path}`);
  } else if (!annotation.placementPosition) {
    states.push("comments list only");
  }
  const replyCount = annotation.replies.length;
  if (replyCount > 0) states.push(`${replyCount} ${replyCount === 1 ? "reply" : "replies"}`);
  if (annotation.isResolved) states.push("resolved");
  const text = annotation.type === "sketch" ? "(drawing)" : quoted(annotation.text, K_TEXT_PREVIEW_MAX);
  const lines = [`annotationId:"${annotation.id}": ${text} (${states.join(", ")})`];

  const replies = annotation.replies.slice(-K_MAX_LISTED_REPLIES);
  if (replyCount > replies.length) lines.push(`  (${replyCount - replies.length} earlier replies)`);
  for (const reply of replies) {
    lines.push(`  reply by ${authorOf(reply.creator, userId)}: ${quoted(reply.message, K_TEXT_PREVIEW_MAX)}`);
  }
  return lines;
}

function authorOf(author: { readonly id: string; readonly name: string }, userId: string): string {
  return author.id === userId ? "you" : author.name || "unknown";
}

/** How a write result names a comment: its opening words, or "a sketch". */
function labelOf(annotation: AnnotationData): string {
  return annotation.type === "sketch" ? "a sketch" : quoted(annotation.text, K_LABEL_PREVIEW_MAX);
}

function quoted(text: string, max: number): string {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length === 0) return "(empty)";
  return `"${flat.length > max ? `${flat.slice(0, max)}...` : flat}"`;
}

/** Milliseconds of an ISO timestamp, 0 for an unreadable one so sorting stays total. */
function timeOf(iso: string): number {
  return Date.parse(iso) || 0;
}

/** The part and every snapshot under it — a hit on any of them is the part. */
function subtreeIds(root: EntitySnapshot): Set<string> {
  const ids = new Set<string>();
  const walk = (snapshot: EntitySnapshot): void => {
    ids.add(snapshot.id);
    for (const child of snapshot.children) walk(child);
  };
  walk(root);
  return ids;
}
